import { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

function ManagePlaces() {
  const [user, setUser] = useState(null);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlace, setEditingPlace] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'Heritage',
    image: '',
    description: '',
    website: ''
  });
  const navigate = useNavigate();

  const categories = ['Heritage', 'Food & Drink', 'Recreation', 'Shopping', 'Services', 'Entertainment'];

  // Check authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Fetch places from Firestore
  useEffect(() => {
    if (user) {
      fetchPlaces();
    }
  }, [user]);

  const fetchPlaces = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'places'));
      const placesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlaces(placesData);
    } catch (error) {
      console.error('Error fetching places:', error);
      alert('Failed to fetch places');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.category || !formData.description) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      if (editingPlace) {
        // Update existing place
        const placeRef = doc(db, 'places', editingPlace.id);
        await updateDoc(placeRef, {
          ...formData,
          updatedAt: serverTimestamp()
        });
        alert('Place updated successfully!');
      } else {
        // Create new place
        await addDoc(collection(db, 'places'), {
          ...formData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        alert('Place added successfully!');
      }
      
      resetForm();
      fetchPlaces();
    } catch (error) {
      console.error('Error saving place:', error);
      alert('Failed to save place: ' + error.message);
    }
  };

  const handleEdit = (place) => {
    setEditingPlace(place);
    setFormData({
      title: place.title,
      category: place.category,
      image: place.image,
      description: place.description,
      website: place.website || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (placeId) => {
    if (!window.confirm('Are you sure you want to delete this place?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'places', placeId));
      alert('Place deleted successfully!');
      fetchPlaces();
    } catch (error) {
      console.error('Error deleting place:', error);
      alert('Failed to delete place');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'Heritage',
      image: '',
      description: '',
      website: ''
    });
    setEditingPlace(null);
    setShowForm(false);
  };

  if (!user) {
    return <div className="py-20 text-center text-white">Checking authentication...</div>;
  }

  return (
    <div className="py-20 min-h-screen">
      <div className="container-width section-padding">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-white">Manage Places</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary"
          >
            {showForm ? 'Cancel' : '+ Add New Place'}
          </button>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">
              {editingPlace ? 'Edit Place' : 'Add New Place'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-600 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Image URL
                </label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-600 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="https://example.com/place-image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-600 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-600 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows="4"
                  required
                />
              </div>

              <div className="flex gap-4">
                <button type="submit" className="btn-primary">
                  {editingPlace ? 'Update Place' : 'Add Place'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 border border-gray-600 text-gray-200 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Places List */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg shadow-lg overflow-hidden">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-white mb-4">All Places</h2>
            
            {loading ? (
              <div className="text-center py-8 text-gray-300">Loading places...</div>
            ) : places.length === 0 ? (
              <div className="text-center py-8 text-gray-300">
                No places found. Add your first place!
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-200">Title</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-200">Category</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-200">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-200">Website</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-200">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {places.map((place) => (
                      <tr key={place.id} className="hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-4 text-sm text-white">{place.title}</td>
                        <td className="px-4 py-4 text-sm">
                          <span className="bg-primary-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                            {place.category}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-300 max-w-md truncate">
                          {place.description}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          {place.website ? (
                            <a
                              href={place.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 truncate block max-w-xs"
                            >
                              Visit Website ↗
                            </a>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-right">
                          <button
                            onClick={() => handleEdit(place)}
                            className="text-blue-400 hover:text-blue-300 font-medium mr-4"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(place.id)}
                            className="text-red-400 hover:text-red-300 font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManagePlaces;
